'use client'

﻿import { useRef, useEffect, useState, useMemo } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { awards, type Award } from '../data/awards'
import { buildNetworkTargets, getNetworkEdges } from '../components/HexField/networkUtils'

gsap.registerPlugin(ScrollTrigger, useGSAP)

// ── Match R3F perspective camera (FOV=75, cameraZ=5) ──
const FOV_RAD = 75 * (Math.PI / 180)
const CAMERA_Z = 5
const VP_H = 2 * Math.tan(FOV_RAD / 2) * CAMERA_Z
const NET_SCALE = 3.2
const NET_OFFSET_Y_FRAC = -0.05

function worldToScreen(
  nx: number,
  ny: number,
  cw: number,
  ch: number,
): [number, number] {
  const aspect = cw / ch
  const vpHalfW = (VP_H * aspect) / 2
  const vpHalfH = VP_H / 2
  const netOffY = VP_H * NET_OFFSET_Y_FRAC
  return [
    (nx * NET_SCALE / vpHalfW + 1) / 2 * cw,
    (1 - (ny * NET_SCALE + netOffY) / vpHalfH) / 2 * ch,
  ]
}

export default function Proof() {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inViewRef = useRef(false)
  const [hovered, setHovered] = useState<number | null>(null)
  const hoveredRef = useRef<number | null>(null)

  const { nodePositions } = useMemo(() => buildNetworkTargets(1), [])
  const edges = useMemo(() => getNetworkEdges(nodePositions), [nodePositions])

  // The PRESTIGE TIER — the first three records (Huawei Grand Prize, Huawei 1st
  // Prize, IEEE publication) get hero dossier cards; the rest flow in the list.
  const FEATURED_COUNT = 3

  // Group the remaining awards into a chronological timeline. The array is authored
  // newest-first, so consecutive same-year entries collapse into one year block —
  // preserving each award's global node index for the hover network.
  const yearGroups = useMemo(() => {
    const out: { year: string; items: { award: Award; index: number }[] }[] = []
    awards.forEach((award, index) => {
      if (index < FEATURED_COUNT) return
      const last = out[out.length - 1]
      if (last && last.year === award.year) last.items.push({ award, index })
      else out.push({ year: award.year, items: [{ award, index }] })
    })
    return out
  }, [])

  useEffect(() => {
    hoveredRef.current = hovered
  }, [hovered])

  // Visibility toggle
  useEffect(() => {
    const el = document.getElementById('proof')
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        inViewRef.current = e.isIntersecting
        if (canvasRef.current)
          canvasRef.current.style.opacity = e.isIntersecting ? '1' : '0'
      },
      { threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Draw edges + node rings
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Particle network is a desktop-only effect — skip the continuous canvas
    // animation loop on mobile to keep the main thread free (Core Web Vitals).
    if (window.innerWidth < 768) return
    let rafId: number
    let scanT = 0

    // Link status per node index (= award array index) — drives node style + label.
    const linkedFlags = awards.map((a) => Boolean(a.url))

    // ── Verification-trace state (lives across frames, no per-frame alloc) ──
    let prevActive: number | null = null
    let traceT = 0 // 0→1 travel of the pulse along the bezier
    let lockT = 0 // frames since the pulse locked the node (for the brief pip)
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    let reduced = reduceMq.matches
    const onReduceChange = (e: MediaQueryListEvent) => {
      reduced = e.matches
    }
    reduceMq.addEventListener('change', onReduceChange)

    const resize = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio
      canvas.height = window.innerHeight * window.devicePixelRatio
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
    }
    resize()
    window.addEventListener('resize', resize)

    const loop = () => {
      if (inViewRef.current) {
        const dpr = window.devicePixelRatio
        const cw = canvas.width / dpr
        const ch = canvas.height / dpr
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        // Confine the network + scan to the Proof section's on-screen area, so this
        // fixed full-viewport canvas never paints over the previous/next section.
        const secEl = document.getElementById('proof')
        const sr = secEl ? secEl.getBoundingClientRect() : { top: 0, height: ch }
        ctx.save()
        ctx.beginPath()
        ctx.rect(0, sr.top * dpr, canvas.width, sr.height * dpr)
        ctx.clip()
        const active = hoveredRef.current
        const pts = nodePositions.map((n) => worldToScreen(n.x, n.y, cw, ch))

        // Edges
        edges.forEach(([i, j]) => {
          const [x1, y1] = pts[i]
          const [x2, y2] = pts[j]
          const hot = active === i || active === j
          // While one record is active, recede the rest of the graph to ~0.5.
          const dim = active !== null && !hot ? 0.5 : 1
          ctx.beginPath()
          ctx.moveTo(x1 * dpr, y1 * dpr)
          ctx.lineTo(x2 * dpr, y2 * dpr)
          // Idle edges at 0.2 (was 0.09 — the network was effectively invisible;
          // the interactivity must be self-evident, not a hover-discovered secret).
          ctx.strokeStyle = hot
            ? 'rgba(255,107,0,0.4)'
            : `rgba(255,107,0,${0.2 * dim})`
          ctx.lineWidth = (hot ? 1.5 : 0.8) * dpr
          ctx.stroke()
        })

        // Node rings — styled by link status: linked records read as SOLID (filled
        // + ring); attested (source-less) records read as HOLLOW. The distinction is
        // the honest visual grammar of the constellation.
        pts.forEach(([px, py], i) => {
          const x = px * dpr
          const y = py * dpr
          const hot = active === i
          const isLinked = linkedFlags[i]
          const dim = active !== null && !hot ? 0.5 : 1
          if (hot) {
            const g = ctx.createRadialGradient(x, y, 0, x, y, 24 * dpr)
            g.addColorStop(0, 'rgba(255,107,0,0.25)')
            g.addColorStop(1, 'rgba(255,107,0,0)')
            ctx.beginPath()
            ctx.arc(x, y, 24 * dpr, 0, Math.PI * 2)
            ctx.fillStyle = g
            ctx.fill()
          }
          const r = (hot ? 4 : 2) * dpr
          if (isLinked) {
            // Filled core + ring
            ctx.beginPath()
            ctx.arc(x, y, r, 0, Math.PI * 2)
            ctx.fillStyle = `rgba(255,107,0,${(hot ? 0.95 : 0.9) * dim})`
            ctx.fill()
            ctx.beginPath()
            ctx.arc(x, y, r + 3 * dpr, 0, Math.PI * 2)
            ctx.strokeStyle = `rgba(255,107,0,${(hot ? 0.8 : 0.4) * dim})`
            ctx.lineWidth = 1 * dpr
            ctx.stroke()
          } else {
            // Hollow — attested but not source-linked
            ctx.beginPath()
            ctx.arc(x, y, r, 0, Math.PI * 2)
            ctx.strokeStyle = `rgba(255,107,0,${(hot ? 0.8 : 0.45) * dim})`
            ctx.lineWidth = 1.2 * dpr
            ctx.stroke()
          }
        })

        // ── VERIFICATION TRACE: an ephemeral, on-demand radial bezier from the
        // hovered/focused row to its constellation node. Read the row rect every
        // frame so it tracks scroll; a pulse travels the path and 'locks' the node. ──
        if (active !== null) {
          if (active !== prevActive) {
            prevActive = active
            traceT = reduced ? 1 : 0
            lockT = 0
          } else if (!reduced && traceT < 1) {
            traceT = Math.min(1, traceT + 0.035)
          } else if (traceT >= 1) {
            lockT += 1
          }

          const rowEl = document.querySelector(`[data-award-index="${active}"]`)
          const target = pts[active]
          if (rowEl && target) {
            const rect = rowEl.getBoundingClientRect()
            const nx = target[0]
            const ny = target[1]
            const rowCx = rect.left + rect.width / 2
            const startX = nx >= rowCx ? rect.right : rect.left
            const startY = rect.top + rect.height / 2
            const dx = nx - startX
            const dy = ny - startY
            const dist = Math.hypot(dx, dy) || 1
            // Radial bow — offset the control points perpendicular to the chord.
            const curve = Math.min(dist * 0.28, 110)
            const ox = (-dy / dist) * curve
            const oy = (dx / dist) * curve
            const c1x = startX + dx * 0.25 + ox
            const c1y = startY + dy * 0.25 + oy
            const c2x = startX + dx * 0.75 + ox
            const c2y = startY + dy * 0.75 + oy
            const isLinked = linkedFlags[active]

            // Trace line
            ctx.beginPath()
            ctx.moveTo(startX * dpr, startY * dpr)
            ctx.bezierCurveTo(
              c1x * dpr, c1y * dpr,
              c2x * dpr, c2y * dpr,
              nx * dpr, ny * dpr,
            )
            ctx.strokeStyle = 'rgba(255,107,0,0.55)'
            ctx.lineWidth = 1 * dpr
            ctx.stroke()

            // Origin pip on the row edge
            ctx.beginPath()
            ctx.arc(startX * dpr, startY * dpr, 2.5 * dpr, 0, Math.PI * 2)
            ctx.fillStyle = 'rgba(255,138,0,0.9)'
            ctx.fill()

            if (!reduced && traceT < 1) {
              // Travelling pulse along the cubic bezier
              const t = traceT
              const u = 1 - t
              const bx = u * u * u * startX + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * nx
              const by = u * u * u * startY + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * ny
              const gx = bx * dpr
              const gy = by * dpr
              const pg = ctx.createRadialGradient(gx, gy, 0, gx, gy, 7 * dpr)
              pg.addColorStop(0, 'rgba(255,184,92,0.95)')
              pg.addColorStop(1, 'rgba(255,138,0,0)')
              ctx.beginPath()
              ctx.arc(gx, gy, 7 * dpr, 0, Math.PI * 2)
              ctx.fillStyle = pg
              ctx.fill()
              ctx.beginPath()
              ctx.arc(gx, gy, 2 * dpr, 0, Math.PI * 2)
              ctx.fillStyle = 'rgba(255,220,170,0.95)'
              ctx.fill()
            } else {
              // Locked — a confirming ring on the node…
              ctx.beginPath()
              ctx.arc(nx * dpr, ny * dpr, 8 * dpr, 0, Math.PI * 2)
              ctx.strokeStyle = 'rgba(255,107,0,0.8)'
              ctx.lineWidth = 1.5 * dpr
              ctx.stroke()
              // …and a brief 'VERIFIED' pip (static under reduced motion).
              const pipAlpha = reduced ? 0.9 : Math.max(0, 1 - lockT / 48)
              if (pipAlpha > 0.01) {
                ctx.font = `${8 * dpr}px ui-monospace, SFMono-Regular, monospace`
                ctx.textBaseline = 'middle'
                ctx.textAlign = 'center'
                ctx.fillStyle = `rgba(255,184,92,${pipAlpha})`
                ctx.fillText('VERIFIED', nx * dpr, ny * dpr + 16 * dpr)
                ctx.textAlign = 'left'
              }
            }

            // Terminate label at the node
            ctx.font = `${9 * dpr}px ui-monospace, SFMono-Regular, monospace`
            ctx.textBaseline = 'middle'
            ctx.textAlign = 'left'
            ctx.fillStyle = isLinked
              ? 'rgba(255,138,0,0.92)'
              : 'rgba(170,170,180,0.75)'
            ctx.fillText(
              isLinked ? 'CROSS-REFERENCED ↗' : 'ATTESTED',
              nx * dpr + 12 * dpr,
              ny * dpr - 12 * dpr,
            )
          }
        } else if (prevActive !== null) {
          // Reset once the hover/focus clears.
          prevActive = null
          traceT = 0
          lockT = 0
        }

        // Verifying sweep — an orange scan passes down the section
        scanT = (scanT + 0.0016) % 1
        const scanY = (sr.top + scanT * sr.height) * dpr
        const band = 44 * dpr
        const sg = ctx.createLinearGradient(0, scanY - band, 0, scanY + band)
        sg.addColorStop(0, 'rgba(255,138,0,0)')
        sg.addColorStop(0.5, 'rgba(255,138,0,0.12)')
        sg.addColorStop(1, 'rgba(255,138,0,0)')
        ctx.fillStyle = sg
        ctx.fillRect(0, scanY - band, canvas.width, band * 2)
        ctx.fillStyle = 'rgba(255,184,92,0.35)'
        ctx.fillRect(0, scanY, canvas.width, 1 * dpr)
        ctx.restore()
      }
      rafId = requestAnimationFrame(loop)
    }
    loop()
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
      reduceMq.removeEventListener('change', onReduceChange)
    }
  }, [nodePositions, edges])

  // GSAP animations
  useGSAP(
    () => {
      if (!containerRef.current) return
      const scope = containerRef.current

      gsap.fromTo(
        scope.querySelector('.proof-header'),
        { y: -20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: scope,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        },
      )

      scope.querySelectorAll<HTMLElement>('.proof-title-word').forEach((el, i) => {
        gsap.fromTo(
          el,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: 'power4.out',
            delay: i * 0.08,
            scrollTrigger: {
              trigger: scope,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          },
        )
      })

      scope.querySelectorAll<HTMLElement>('.award-row').forEach((el, i) => {
        gsap.fromTo(
          el,
          { x: -30, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.5,
            ease: 'power3.out',
            delay: i * 0.025,
            scrollTrigger: {
              trigger: el,
              start: 'top 92%',
              toggleActions: 'play none none reverse',
            },
          },
        )
      })

      gsap.to(scope.querySelector('.proof-watermark'), {
        rotation: 15,
        ease: 'none',
        scrollTrigger: {
          trigger: scope,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      })
    },
    { scope: containerRef },
  )

  const hoveredAward = hovered !== null ? awards[hovered] : null
  // ── HONESTY: derive the real cross-reference count from the data itself ──
  // linked = records that carry a public source URL (=10); the rest are attested.
  const linked = awards.filter((a) => a.url).length
  const edge = 'clamp(1.5rem, 5vw, 4rem)'

  return (
    <section
      ref={containerRef}
      id="proof"
      data-scene="proof"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: 'var(--color-abyss)', minHeight: '100vh' }}
    >
      {/* Fixed overlay canvas */}
      <canvas
        ref={canvasRef}
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2, opacity: 0, transition: 'opacity 0.6s ease' }}
        aria-hidden="true"
      />

      {/* Watermark */}
      <div
        className="proof-watermark absolute pointer-events-none select-none font-display font-extrabold whitespace-nowrap"
        style={{ top: '38%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 'clamp(20rem, 50vw, 60rem)', color: 'var(--color-fire)', opacity: 0.03, lineHeight: 1, letterSpacing: '-0.06em', zIndex: 0 }}
        aria-hidden="true"
      >
        {awards.length}
      </div>

      {/* ── HUD header — section label + verification status (coherent with Forge Terminal) ── */}
      <div
        className="proof-header absolute flex items-center justify-between gap-4"
        style={{ top: 'clamp(5rem, 8vh, 6.5rem)', left: edge, right: edge, zIndex: 10 }}
      >
        <div className="flex items-center gap-3">
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>06</span>
          <svg width={10} height={10} viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
            <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke="var(--color-fire)" strokeWidth="8" />
          </svg>
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>The Proof</span>
          <a href="/awards" className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)', textDecoration: 'none' }} data-cursor-label="Full record">/ full record →</a>
        </div>
        <div className="hidden lg:flex items-center gap-3 font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>
          <span>{String(awards.length).padStart(2, '0')} RECORDS</span>
          <span aria-hidden style={{ width: 1, height: 10, background: 'var(--color-border)' }} />
          <span className="flex items-center gap-1.5"><span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 8px var(--color-fire)', animation: 'dot-pulse 2.2s ease-in-out infinite' }} /><span style={{ color: 'var(--color-fire)' }}>{linked}</span> / {awards.length} CROSS-REFERENCED TO SOURCE</span>
        </div>
      </div>

      {/* ── Hero block: title + counter, centered in left 55% ── */}
      <div
        className="relative flex flex-col"
        style={{ zIndex: 5, paddingTop: 'clamp(9rem, 14vh, 12rem)', paddingLeft: edge, paddingRight: edge, width: '55%', minWidth: 'min(100%, 36rem)' }}
      >
        {/* Big title — single line. Sized to THIS column (cqi) so the single
            unbreakable word "RECOGNITION." always fits its 55% track at every width. */}
        <div className="proof-title-word mb-8" style={{ overflow: 'hidden', containerType: 'inline-size' }}>
          <span
            className="font-display block"
            style={{ fontSize: 'clamp(1rem, 7.5cqi, 7.5rem)', fontWeight: 800, lineHeight: 0.9, letterSpacing: '-0.04em', color: 'var(--color-light)', whiteSpace: 'nowrap' }}
          >
            RECOGNITION.
          </span>
        </div>

        {/* Verified status — honest, data-derived: how many records carry a public source */}
        <div className="flex items-center gap-2.5 mb-8 font-mono uppercase" style={{ fontSize: 'clamp(0.55rem, 0.8vw, 0.7rem)', letterSpacing: '0.22em' }}>
          <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 8px var(--color-fire)' }} />
          <span style={{ color: 'var(--color-fire)' }}>{linked} / {awards.length} cross-referenced to source</span>
          <span style={{ color: 'var(--color-stone)' }}>· {awards.length - linked} attested</span>
        </div>

        {/* Counter — centered in the viewport */}
        <div className="flex items-center gap-0 mb-10" style={{ alignSelf: 'center', width: '100%', justifyContent: 'center' }}>
          <span
            className="font-display tabular-nums"
            style={{ fontSize: 'clamp(4rem, 10vw, 9rem)', fontWeight: 700, color: 'var(--color-fire)', lineHeight: 1, letterSpacing: '-0.04em' }}
          >
            {awards.length}
          </span>
          <div style={{ marginLeft: '1.2rem' }}>
            <span className="font-mono uppercase block" style={{ fontSize: 'clamp(0.5rem, 0.75vw, 0.65rem)', letterSpacing: '0.2em', color: 'var(--color-stone)', lineHeight: 1.6 }}>recognitions</span>
            <span className="font-mono uppercase block" style={{ fontSize: 'clamp(0.5rem, 0.75vw, 0.65rem)', letterSpacing: '0.2em', color: 'var(--color-stone)', lineHeight: 1.6 }}>and awards</span>
          </div>
        </div>
      </div>

      {/* ── Full-width separator ── */}
      <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)', margin: `0 ${edge}`, zIndex: 5, position: 'relative' }} />

      {/* ── PRESTIGE TIER — the three defining records as hero dossier cards ── */}
      <div
        className="relative"
        style={{ zIndex: 5, padding: `clamp(2rem,4vh,3rem) ${edge} 0`, maxWidth: '92rem', margin: '0 auto', width: '100%' }}
      >
        <div className="flex items-center gap-2.5 font-mono uppercase" style={{ marginBottom: 'clamp(1rem, 2vh, 1.5rem)', fontSize: 'clamp(0.55rem, 0.75vw, 0.68rem)', letterSpacing: '0.24em', color: 'var(--color-stone)' }}>
          <span style={{ color: 'var(--color-fire)' }}>//</span>
          <span>Featured records</span>
          <span aria-hidden style={{ flex: 1, height: 1, background: 'rgba(255,107,0,0.12)' }} />
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: 'clamp(0.9rem, 1.8vw, 1.5rem)' }}
        >
          {awards.slice(0, FEATURED_COUNT).map((award, index) => {
            const active = hovered === index
            const hasLink = Boolean(award.url)
            const Tag = hasLink ? 'a' : 'div'
            const linkProps = hasLink
              ? { href: award.url, target: '_blank' as const, rel: 'noopener noreferrer' }
              : {}
            return (
              <Tag
                key={award.id}
                {...linkProps}
                data-award-index={index}
                className="award-row relative"
                style={{
                  textDecoration: 'none',
                  border: active ? '1px solid rgba(255,107,0,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  background: active ? 'rgba(255,107,0,0.05)' : 'rgba(255,255,255,0.015)',
                  transition: 'border-color 0.3s, background 0.3s, transform 0.3s',
                  transform: active ? 'translateY(-3px)' : 'none',
                  cursor: hasLink ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                tabIndex={hasLink ? undefined : 0}
              >
                {/* HUD corners */}
                <span aria-hidden style={{ position: 'absolute', top: -1, left: -1, width: 12, height: 12, borderTop: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)', opacity: 0.7 }} />
                <span aria-hidden style={{ position: 'absolute', top: -1, right: -1, width: 12, height: 12, borderTop: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)', opacity: 0.7 }} />
                <span aria-hidden style={{ position: 'absolute', bottom: -1, left: -1, width: 12, height: 12, borderBottom: '1px solid var(--color-fire)', borderLeft: '1px solid var(--color-fire)', opacity: 0.7 }} />
                <span aria-hidden style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, borderBottom: '1px solid var(--color-fire)', borderRight: '1px solid var(--color-fire)', opacity: 0.7 }} />

                {/* Record image */}
                {award.image && (
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden' }}>
                    <img
                      src={award.image}
                      alt={award.title}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: active ? 'none' : 'saturate(0.85)', transition: 'filter 0.3s' }}
                    />
                    <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(5,5,5,0.55), transparent 45%)' }} />
                  </div>
                )}

                {/* Dossier body */}
                <div style={{ padding: 'clamp(0.85rem, 1.6vw, 1.2rem)', display: 'flex', flexDirection: 'column', gap: '0.55rem', flex: 1 }}>
                  <div className="flex items-center justify-between font-mono uppercase" style={{ fontSize: 'clamp(0.5rem, 0.65vw, 0.58rem)', letterSpacing: '0.2em' }}>
                    <span style={{ color: active ? 'var(--color-fire)' : 'var(--color-stone)', transition: 'color 0.25s' }}>
                      Node {String(index + 1).padStart(2, '0')} · {award.year}
                    </span>
                    {hasLink ? (
                      <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-fire)' }}>
                        <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 6px var(--color-fire)' }} />
                        Cross-referenced ↗
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-stone)', opacity: 0.7 }}>
                        <span aria-hidden style={{ width: 5, height: 5, borderRadius: '50%', border: '1px solid var(--color-stone)' }} />
                        Attested
                      </span>
                    )}
                  </div>
                  <p className="font-body leading-snug" style={{ margin: 0, fontSize: 'clamp(0.88rem, 1.1vw, 1.02rem)', color: active ? 'var(--color-light)' : 'var(--color-cloud, var(--color-mist))', transition: 'color 0.25s' }}>
                    {award.title}
                  </p>
                  {hasLink && (
                    <span className="font-mono uppercase" style={{ marginTop: 'auto', fontSize: '0.55rem', letterSpacing: '0.2em', color: active ? 'var(--color-fire)' : 'var(--color-stone)', transition: 'color 0.25s' }}>
                      Open record ↗
                    </span>
                  )}
                </div>
              </Tag>
            )
          })}
        </div>
      </div>

      {/* ── Awards grid — 2 columns ── */}
      <div
        className="relative"
        style={{ zIndex: 5, padding: `clamp(2rem,4vh,3rem) ${edge} clamp(4rem,8vh,6rem)`, maxWidth: '92rem', margin: '0 auto', width: '100%' }}
      >
        <div className="flex flex-col" style={{ gap: 'clamp(1.5rem, 3vh, 2.5rem)' }}>
          {yearGroups.map((group) => (
            <div
              key={group.year}
              className="award-group flex flex-col md:flex-row"
              style={{ gap: 'clamp(0.5rem, 2vw, 2rem)' }}
            >
              {/* Year rail */}
              <div
                className="flex-shrink-0 flex md:flex-col items-baseline md:items-start gap-3 md:gap-1.5 md:w-44"
                style={{ paddingTop: 'clamp(0.85rem, 1.6vh, 1.2rem)' }}
              >
                <span
                  className="font-display"
                  style={{ fontSize: 'clamp(1.4rem, 2.4vw, 2rem)', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--color-light)' }}
                >
                  {group.year}
                </span>
                <span
                  className="font-mono uppercase"
                  style={{ fontSize: 'clamp(0.45rem, 0.6vw, 0.55rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}
                >
                  {group.items.length} {group.items.length === 1 ? 'award' : 'awards'}
                </span>
              </div>

              {/* This year's awards */}
              <div
                className="flex-1 md:border-l"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))',
                  gap: '0',
                  borderColor: 'rgba(255,255,255,0.06)',
                }}
              >
                {group.items.map(({ award, index }) => {
                  const active = hovered === index
                  const hasLink = Boolean(award.url)
                  const Tag = hasLink ? 'a' : 'div'
                  const linkProps = hasLink
                    ? { href: award.url, target: '_blank' as const, rel: 'noopener noreferrer' }
                    : {}

                  return (
                    <Tag
                      key={award.id}
                      {...linkProps}
                      data-award-index={index}
                      className="award-row"
                      style={{
                        borderTop: '1px solid rgba(255,255,255,0.055)',
                        padding: 'clamp(1rem,2vh,1.4rem) clamp(0.85rem,1.6vw,1.4rem)',
                        textDecoration: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        cursor: hasLink ? 'pointer' : 'default',
                        transition: 'background-color 0.25s',
                        backgroundColor: active ? 'rgba(255,107,0,0.04)' : 'transparent',
                      }}
                      onMouseEnter={() => setHovered(index)}
                      onMouseLeave={() => setHovered(null)}
                      onFocus={() => setHovered(index)}
                      onBlur={() => setHovered(null)}
                      tabIndex={hasLink ? undefined : 0}
                    >
                      {/* Top row: node index + link arrow */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span
                          className="font-mono"
                          style={{
                            fontSize: 'clamp(0.48rem, 0.6vw, 0.55rem)',
                            letterSpacing: '0.15em',
                            color: active ? 'var(--color-fire)' : 'var(--color-stone)',
                            transition: 'color 0.25s',
                          }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        {hasLink ? (
                          <span
                            className="font-mono uppercase"
                            style={{
                              fontSize: 'clamp(0.42rem, 0.55vw, 0.5rem)',
                              letterSpacing: '0.16em',
                              whiteSpace: 'nowrap',
                              color: active ? 'var(--color-fire)' : 'var(--color-stone)',
                              transition: 'color 0.25s',
                              flexShrink: 0,
                            }}
                          >
                            Cross-referenced ↗
                          </span>
                        ) : (
                          <span
                            className="font-mono uppercase"
                            style={{
                              fontSize: 'clamp(0.42rem, 0.55vw, 0.5rem)',
                              letterSpacing: '0.16em',
                              whiteSpace: 'nowrap',
                              color: 'var(--color-stone)',
                              opacity: active ? 0.85 : 0.5,
                              transition: 'opacity 0.25s',
                              flexShrink: 0,
                            }}
                          >
                            Attested
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <p
                        className="font-body leading-snug"
                        style={{
                          fontSize: 'clamp(0.78rem, 1vw, 0.92rem)',
                          color: active ? 'var(--color-light)' : 'var(--color-mist)',
                          transition: 'color 0.25s',
                          margin: 0,
                        }}
                      >
                        {award.title}
                      </p>
                    </Tag>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center gap-3">
          <div className="h-px flex-1" style={{ backgroundColor: 'rgba(255,107,0,0.1)' }} />
          <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.48rem, 0.65vw, 0.58rem)', letterSpacing: '0.2em', color: 'var(--color-stone)' }}>
            <span style={{ color: 'var(--color-fire)' }}>{awards.length}</span> nodes · recognition network
          </span>
          <div className="h-px flex-1" style={{ backgroundColor: 'rgba(255,107,0,0.1)' }} />
        </div>
      </div>

      {/* Floating image preview — fixed bottom-right */}
      <div
        style={{
          position: 'fixed',
          bottom: '2.5rem',
          right: '2.5rem',
          width: 'clamp(200px, 18vw, 300px)',
          aspectRatio: '16 / 10',
          borderRadius: '6px',
          overflow: 'hidden',
          zIndex: 50,
          pointerEvents: 'none',
          opacity: hoveredAward?.image ? 1 : 0,
          transform: hoveredAward?.image ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.96)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          border: '1px solid rgba(255,107,0,0.15)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.75)',
        }}
      >
        {hoveredAward?.image && (
          <img
            key={hoveredAward.image}
            src={hoveredAward.image}
            alt={hoveredAward.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 55%)',
            display: 'flex',
            alignItems: 'flex-end',
            padding: '0.65rem',
          }}
        >
          <span
            className="font-mono"
            style={{ fontSize: '0.5rem', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.65)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {hoveredAward?.title}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px" style={{ backgroundColor: 'rgba(255,107,0,0.15)', marginLeft: 'clamp(1.5rem, 4vw, 5rem)', marginRight: 'clamp(1.5rem, 4vw, 5rem)' }} />
    </section>
  )
}
